/**
 * Centralized react-query keys for the notifications domain.
 * Hierarchical tuples per the plan's §10.2 conventions.
 */
import type { AuditListParams } from './api/notificationsApi';
import type { AlertSeverity, AdminAuditTargetType } from './types';

export const notificationsKeys = {
  all: ['notifications'] as const,

  dashboard: () => [...notificationsKeys.all, 'dashboard'] as const,
  dashboardMetrics: (day?: string) =>
    [...notificationsKeys.dashboard(), 'metrics', day ?? 'today'] as const,
  dashboardTimeseries: (days: number) =>
    [...notificationsKeys.dashboard(), 'timeseries', days] as const,

  decisionsTimeseries: (params: { days?: number; journeyId?: string }) =>
    [...notificationsKeys.all, 'decisions', 'timeseries', params] as const,

  config: () => [...notificationsKeys.all, 'config'] as const,

  journeys: () => [...notificationsKeys.all, 'journeys'] as const,
  journey: (journeyId: string) => [...notificationsKeys.journeys(), journeyId] as const,

  users: () => [...notificationsKeys.all, 'users'] as const,
  userProfile: (userId: string) => [...notificationsKeys.users(), userId, 'profile'] as const,

  alerts: () => [...notificationsKeys.all, 'alerts'] as const,
  alertPolicies: () => [...notificationsKeys.alerts(), 'policies'] as const,
  alertFirings: (filters: {
    policyKey?: string;
    severity?: AlertSeverity;
    since?: string;
    limit?: number;
  }) => [...notificationsKeys.alerts(), 'firings', filters] as const,

  audit: (params?: AuditListParams) =>
    params
      ? ([...notificationsKeys.all, 'audit', normalizeAuditKey(params)] as const)
      : ([...notificationsKeys.all, 'audit'] as const),
};

function normalizeAuditKey(params: AuditListParams) {
  return {
    actorId: params.actorId ?? null,
    targetType: params.targetType ?? null,
    targetId: params.targetId ?? null,
    since: params.since ?? null,
    limit: params.limit ?? null,
    cursor: params.cursor ?? null,
  } as const;
}

export type TargetType = AdminAuditTargetType;
